import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useIndexApi, usePostApi, usePutApi, useShowApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { TemperatureLedgerMaster, TemperatureLedgerForm, TemperatureLedgerSearchForm } from 'entities/temperature_ledger'

type TemperatureLedgersResponse = PagingResponse & {
    results: TemperatureLedgerMaster[]
}

export function useFetchTemperatureLedgersApi(
    searchForm?: Form<TemperatureLedgerSearchForm>
): IndexApiSet<TemperatureLedgersResponse> & { execute: () => void } {
    const apiPath = 'temperature_ledgers/'
    const api = useIndexApi<TemperatureLedgersResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        execute()
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

type TemperatureLedgerMasterResponse = BaseResponse & {
    temperatureLedgerMaster?: TemperatureLedgerMaster
}

export function useFetchFreezerApi(): ApiSet<TemperatureLedgerMasterResponse> & {
    execute: (id: number) => void
} {
    const api = useShowApi<TemperatureLedgerMasterResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `temperature_ledgers/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

export function usePostTemperatureLedgerApi(): ApiSet<BaseResponse> & { execute: (form: Form<TemperatureLedgerForm>) => void } {
    const apiSet = usePostApi<BaseResponse, TemperatureLedgerForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<TemperatureLedgerForm>) => {
        const apiPath = `temperature_ledgers/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutTemperatureLedgerApi(): ApiSet<BaseResponse> & { execute: (form: Form<TemperatureLedgerForm>) => void } {
    const apiSet = usePutApi<BaseResponse, TemperatureLedgerForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<TemperatureLedgerForm>) => {
        const apiPath = `temperature_ledgers/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutTemperatureLedgerApi(): ApiSet<BaseResponse> & { execute: (form: Form<TemperatureLedgerForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, TemperatureLedgerForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<TemperatureLedgerForm[]>) => {
        const apiPath = `temperature_ledgers/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteTemperatureLedgerApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `temperature_ledgers/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
